import React, { useMemo, useState } from 'react';
import cx from 'classnames';
import { ChevronLeft, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  ReportEntity,
  SprintEntity,
  SprintProductReportsParams,
  useCreatePmReport,
  useListSprints,
  useRemovePmReport,
  useSprintProductReports,
  useUpdatePmReport,
} from '@data';
import { transformSprintProductReportsData } from '@data/api/utils/helpers';
import { useOrganizationChapters } from '@data/hooks/organization';
import { Loader } from '@ui';
import { Switch } from '@app/components/core';
import { tabClass } from '@app/constants/styles';
import ReportIncidentDialog from '@app/components/shared/phillReport/ReportIncidentDialog';
import RemoveReportModal from '@app/components/shared/removeReportModal';
import DayLog from '../clientReporting/sprintProduct/DayLog';

/** One document in the batch: the product and sprint whose entries it is built from. */
export interface EntriesFile {
  key: string;
  productId: string;
  sprintId: string;
  /** How the send dialog names this document, so both call it the same thing. */
  label: string;
}

/** One product's sprint as reported, every entry correctable: a wrong hour is as often a wrong line as a wrong number. */
const ProductSprintLog = ({
  file,
  sprint,
  organizationId,
  expandAll,
  onCorrected,
}: {
  file: EntriesFile;
  sprint?: SprintEntity;
  organizationId: string;
  expandAll: boolean;
  onCorrected: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const params: SprintProductReportsParams = {
    sprint,
    productId: file.productId,
    chapters: undefined,
    incidentWork: true,
  };
  const { data: reports } = useSprintProductReports(params);
  // includeDisabled: label historical involvements pointing at chapters an admin has since disabled.
  const { data: chapters } = useOrganizationChapters(organizationId, { includeDisabled: true });

  const { mutateAsync: createReportAsync } = useCreatePmReport(organizationId, params);
  const { mutateAsync: updateReportAsync } = useUpdatePmReport(params);
  const { mutate: removeReport } = useRemovePmReport(params);

  const [workDayToAdd, setWorkDayToAdd] = useState<string>();
  const [reportToEdit, setReportToEdit] = useState<ReportEntity>();
  const [reportToRemove, setReportToRemove] = useState<ReportEntity>();

  const { detailed } = useMemo(() => transformSprintProductReportsData(false, sprint, reports), [sprint, reports]);
  const days = useMemo(
    () =>
      (detailed ?? [])
        .filter(day => day.totalMinutes > 0)
        .slice()
        .sort((a, b) => a.date.localeCompare(b.date)),
    [detailed],
  );

  if (!detailed) {
    return <Loader />;
  }

  return (
    <>
      {days.length ? (
        <DayLog
          days={days}
          chapters={chapters?.items}
          expandAll={expandAll}
          // Incident time is not held by its sprint, which is what lets this panel correct a closed one.
          authoring={{
            onAddForDay: setWorkDayToAdd,
            onEditReport: setReportToEdit,
            onRemoveReport: setReportToRemove,
          }}
        />
      ) : (
        <p className="py-10 text-center text-sm text-muted-foreground">{t('noTimeRegistrations')}</p>
      )}

      {/* Adding here opens the incident form, not the ordinary one: every line in this panel is incident
          work, and the entry form's own Incident tab is not on this branch. */}
      {workDayToAdd && (
        <ReportIncidentDialog
          show
          onHide={() => setWorkDayToAdd(undefined)}
          prefill={{ productId: file.productId, workday: new Date(workDayToAdd) }}
          onSubmit={async values => {
            await createReportAsync(values);
            setWorkDayToAdd(undefined);
            onCorrected();
          }}
        />
      )}

      {/* Incident hours are corrected in the form they were reported in, the same as on the Reporting
          page and the member's own: a day, two clock times and which incident, rather than 2.5 hours
          and a link. */}
      <ReportIncidentDialog
        show={!!reportToEdit?.incidentId}
        onHide={() => setReportToEdit(undefined)}
        userId={reportToEdit?.involvement?.membership?.user?.id}
        report={reportToEdit?.incidentId ? reportToEdit : null}
        // The entries come back without a product, and the incident picker has nothing to offer without one.
        against={{ productId: file.productId, incidentId: reportToEdit?.incidentId ?? '' }}
        onSubmit={async values => {
          await updateReportAsync(values);
          onCorrected();
        }}
      />

      <RemoveReportModal
        show={!!reportToRemove}
        onHide={() => setReportToRemove(undefined)}
        onRemove={() => {
          if (reportToRemove) removeReport(reportToRemove.id, { onSuccess: onCorrected });
          setReportToRemove(undefined);
        }}
      />
    </>
  );
};

/** The entries behind a batch of reports, as a detour over the send dialog that gives them back as they were. */
const IncidentReportEntriesPanel = ({
  organizationId,
  files,
  activeKey,
  onActiveKeyChange,
  onBack,
  onCorrected,
}: {
  organizationId: string;
  files: EntriesFile[];
  activeKey?: string;
  onActiveKeyChange: (key: string) => void;
  onBack: () => void;
  onCorrected: (file: EntriesFile) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  // Open on arrival: this is here to be read for what is wrong, not clicked through a day at a time.
  const [expandAll, setExpandAll] = useState(true);

  const { data: sprintData } = useListSprints();
  const active = files.find(file => file.key === activeKey) ?? files[0];
  const sprint = sprintData?.items?.find(item => item.id === active?.sprintId);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="mb-3 flex items-start gap-2">
        {/* The way back is on the panel as well as in the footer: this is a detour, and the arrow is
            what says so at the moment of arriving. */}
        <button
          type="button"
          onClick={onBack}
          aria-label={t('incidentReports.backToReport')}
          className="-ml-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <ChevronLeft className="size-4" aria-hidden />
        </button>
        <div>
          <h2 className="text-base font-medium text-foreground">{t('incidentReports.editTitle')}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{t('incidentReports.editHint')}</p>
        </div>
      </div>

      {/*
        The app's own tab row rather than the preview's document tabs: these change the entries below
        them, which is what a tab underlined against the content it belongs to says and what a floating
        row of document tabs does not. Shown even for a single document, because it also names the one
        being corrected.
      */}
      <div className="mb-3 flex items-end gap-6 border-b border-border">
        {files.map(file => (
          <button
            key={file.key}
            type="button"
            onClick={() => onActiveKeyChange(file.key)}
            className={cx('inline-flex items-center gap-1.5', tabClass(file.key === active?.key))}
          >
            {/* Each tab still wears the document's own mark: these are the entries behind a file. */}
            <FileText className="size-3.5 shrink-0" aria-hidden />
            {file.label}
          </button>
        ))}
        <div className="ml-auto pb-3">
          <Switch value={expandAll} onChange={() => setExpandAll(value => !value)} text={t('titles.expandAll')} />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {active ? (
          <ProductSprintLog
            key={active.key}
            file={active}
            sprint={sprint}
            organizationId={organizationId}
            expandAll={expandAll}
            onCorrected={() => onCorrected(active)}
          />
        ) : (
          <p className="py-10 text-center text-sm text-muted-foreground">{t('incidentReports.pickSprints')}</p>
        )}
      </div>
    </div>
  );
};

export default IncidentReportEntriesPanel;
